/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { useEffect, useState } from 'react';
import { useStoreState } from '@/store';
import { LoadingSpinner, PayPalLogoPP3 } from '@checkout/react-components';
import { boolean } from 'prop-types';
import { features } from '@/lib';

// Internal modules
import { useGetIndex, useVenmo } from './hooks.js';
import { isNextGenSubscription } from '@/lib/globals';
import { LazyCartRender } from '../Billing/Components';
import { useCookieBannerHeight, useMountLogging } from '@/lib/reactHooks';
import { getContent, getGlobals, getLocale, logger } from '@/lib';
import { logShowCartOnce } from './utils';
import { useGetSubscriptionDetails } from './CartDropdown/subscriptionUtils';
import {
  CartAmount,
  CartButton,
  FlexboxContainer,
  HeaderBackground,
  HeaderContainer,
  LogoContainer,
  StyledHeader,
} from './StyledComponents';
import { LogoV8 } from './logo';

const loadLazyCartRender = () => {
  LazyCartRender.preload();
};

const Header = ({ showCartButton = true }) => {
  useMountLogging('Header');
  const { country: globalsCountry, locale } = getLocale();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [shouldLoadCartDropdown, setShouldLoadCartDropdown] = useState(false);
  const [isCartDropdown, setIsCartDropdown] = useState(false);
  const venmoMessage = useVenmo();

  const countriesList =
    useStoreState(({ api }) => api.griffinMetadata.territories[locale]) || [];
  const showLoadingSpinnerWithMessage = useStoreState(
    ({ ui }) => ui.showLoadingSpinnerWithMessage,
  );
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const cartDetails = useStoreState(({ api }) => api.cartDetails);
  const subscriptionDetails = useGetSubscriptionDetails();
  const isNextGenSub = isNextGenSubscription();
  const total =
    (isNextGenSub
      ? subscriptionDetails?.initialPaymentInfo?.totalAmount
      : cartDetails?.amounts?.total) || {};
  const cartAmount = total?.currencyFormat;
  /*
    Cart Dropdown is applicable only in the presence of:
    - A merchant message "note"
    - Descriptions for cart items
    - Breakdown of multiple cart items
    - Subscription cart details for EC-Subscriptions
  */
  useEffect(() => {
    setShouldLoadCartDropdown(
      Boolean(
        cartDetails?.amounts &&
          (cartDetails?.noteToBuyer ||
            cartDetails?.items?.length ||
            cartDetails?.description),
      ) || isNextGenSub,
    );
  }, [cartDetails, isNextGenSub]);

  const handleClick = () => {
    !isCartDropdown && logShowCartOnce(cartDetails?.items?.length, total);
    logger.info(`weasley_cart_dropdown_${isCartDropdown ? 'hide' : 'show'}`);
    setIsCartDropdown(!isCartDropdown);
  };

  const getIndex = useGetIndex(countriesList, globalsCountry);
  useEffect(() => {
    if (selectedIndex === -1 && countriesList?.length > 0) {
      setSelectedIndex(getIndex);
    }
  }, [countriesList]);

  const { userAction } = getGlobals().initialGqlData?.checkoutSession || {};
  useEffect(() => {
    logger.fpti('weasley_cart_dropdown', {
      state_name: 'header',
      transition_name: 'prepare_cart',
      user_action: userAction,
    });
  }, []);

  const cookieBannerHeight = useCookieBannerHeight();
  const shouldUseV8Logo = features.isActive('shouldUsePayPalRebrandStyles');
  const getPPLogo = () => {
    const Component = shouldUseV8Logo ? LogoV8 : PayPalLogoPP3;
    return <Component />;
  };

  return (
    <HeaderContainer data-testid="header">
      <HeaderBackground cookieBannerHeight={cookieBannerHeight}>
        {venmoMessage}
        <StyledHeader>
          <LogoContainer addTopMargin={shouldUseV8Logo}>
            {getPPLogo()}
          </LogoContainer>
          {total?.currencyFormatSymbolISOCurrency && showCartButton && (
            <FlexboxContainer flexPosition="right">
              {showLoadingSpinnerWithMessage ? (
                <LoadingSpinner
                  message={getContent(showLoadingSpinnerWithMessage)}
                />
              ) : (
                <CartButton
                  data-testid="cart-button"
                  onClick={handleClick}
                  shouldLoadCartDropdown={true}
                  onMouseOver={loadLazyCartRender}
                  disabled={isFormSubmitting}
                  aria-label={getContent('cartDropdown', {
                    amount: cartAmount,
                  })}
                  aria-expanded={isCartDropdown}
                >
                  <CartAmount>{cartAmount}</CartAmount>
                </CartButton>
              )}
            </FlexboxContainer>
          )}
        </StyledHeader>
      </HeaderBackground>
      {isCartDropdown && (
        <LazyCartRender
          cartDetails={cartDetails}
          isNextGenSubscription={isNextGenSub}
          onClose={() => setIsCartDropdown(false)}
          shouldLoadCartDropdown={shouldLoadCartDropdown}
        />
      )}
    </HeaderContainer>
  );
};

Header.propTypes = {
  showCartButton: boolean,
};

export { Header };
